import { cva, type VariantProps } from 'class-variance-authority';

/**
 * HEN Carousel item variants using HEN Design System tokens.
 * All styling references design tokens defined in tailwind.config.ts.
 */
export const carouselItemVariants = cva(['min-w-0 shrink-0 grow-0'], {
  variants: {
    basis: {
      full: 'basis-full',
      half: 'basis-1/2',
      third: 'basis-1/3',
      quarter: 'basis-1/4',
    },
  },
  defaultVariants: {
    basis: 'full',
  },
});

export type CarouselItemVariantProps = VariantProps<
  typeof carouselItemVariants
>;

/**
 * Carousel navigation button (previous/next) variants.
 * - `default`: solid black fill + white text on hover
 * - `outline`: transparent background, border only; no fill on hover
 */
export const carouselNavButtonVariants = cva('static h-6 w-6 rounded-full', {
  variants: {
    design: {
      default:
        'border-black hover:bg-black hover:text-white hover:border-white',
      outline:
        'bg-transparent border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:border-neutral-900 hover:text-neutral-200',
    },
  },
  defaultVariants: {
    design: 'default',
  },
});

export type CarouselNavButtonVariantProps = VariantProps<
  typeof carouselNavButtonVariants
>;

/**
 * Carousel dot variants.
 * - `default`: filled solid dot; inactive = neutral-100
 * - `outline`: bordered ring dot; inactive = transparent with border
 */
export const carouselDotVariants = cva(
  [
    'h-2.5 w-2.5 rounded-full transition-all',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 focus-visible:ring-offset-2',
  ],
  {
    variants: {
      design: {
        default: '',
        outline: '',
      },
      active: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      { design: 'default', active: true, class: 'bg-neutral-900' },
      {
        design: 'default',
        active: false,
        class: 'bg-neutral-100 hover:bg-neutral-200',
      },
      {
        design: 'outline',
        active: true,
        class: 'bg-neutral-900 border border-neutral-900',
      },
      {
        design: 'outline',
        active: false,
        class: 'bg-transparent border border-neutral-900 hover:bg-neutral-200',
      },
    ],
    defaultVariants: {
      design: 'default',
      active: false,
    },
  },
);

export type CarouselDotVariantProps = VariantProps<typeof carouselDotVariants>;
